import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { FButton } from "@/components/forkd/FButton";
import { FInput } from "@/components/forkd/FInput";
import { Wordmark } from "@/components/forkd/Wordmark";
import { onboarding } from "@/lib/onboarding";

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

export default function SignUp() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const checkUsername = async () => {
    const u = username.trim().toLowerCase();
    if (!u) return;
    if (!USERNAME_RE.test(u)) {
      setUsernameError("3–20 chars, lowercase letters, numbers, underscores");
      return;
    }
    const { data, error } = await supabase
      .from("profiles")
      .select("id")
      .eq("username", u)
      .maybeSingle();
    if (error) return;
    if (data) {
      setUsernameError("Username is already taken");
    } else {
      setUsernameError(null);
    }
  };

  const onGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    if (error) toast.error(error.message);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const u = username.trim().toLowerCase();
    if (!USERNAME_RE.test(u)) {
      setUsernameError("3–20 chars, lowercase letters, numbers, underscores");
      return;
    }
    if (usernameError) return;
    if (!displayName.trim()) {
      setFormError("Display name is required");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { username: u, display_name: displayName.trim() },
      },
    });
    setLoading(false);
    if (error) {
      setFormError(error.message);
      return;
    }
    onboarding.reset();
    toast.success("Account created");
    nav("/onboarding", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[var(--colour-background)] px-5 pt-12 pb-10 flex flex-col">
      <div className="flex justify-center mb-8">
        <Wordmark size={40} />
      </div>

      <div className="space-y-3 mb-6">
        <FButton variant="outline" fullWidth type="button" onClick={onGoogle}>
          Sign up with Google
        </FButton>
        <FButton variant="outline" fullWidth disabled type="button" title="Coming soon">
          Sign up with Apple
        </FButton>
      </div>

      <div className="flex items-center gap-3 my-2">
        <div className="flex-1 h-px bg-[var(--colour-border)]" />
        <span className="text-[12px] text-[var(--colour-text-secondary)]">or</span>
        <div className="flex-1 h-px bg-[var(--colour-border)]" />
      </div>

      <form onSubmit={onSubmit} className="space-y-3 mt-4">
        <FInput
          name="username"
          label="Username"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value.toLowerCase().replace(/\s+/g, ""));
            setUsernameError(null);
          }}
          onBlur={checkUsername}
          placeholder="yourname"
          required
          error={usernameError}
          hint={usernameError ? undefined : "Lowercase, 3–20 chars, no spaces"}
        />
        <FInput
          name="displayName"
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your name"
          required
        />
        <FInput
          name="email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        <FInput
          name="password"
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          error={formError}
        />
        <FButton type="submit" fullWidth loading={loading}>
          Create account
        </FButton>
      </form>

      <p className="mt-auto pt-8 text-center text-[14px] text-[var(--colour-text-secondary)]">
        Already have an account?{" "}
        <Link to="/signin" className="text-[var(--colour-accent)] font-semibold">
          Sign in
        </Link>
      </p>
    </div>
  );
}
